import type { EmployeeHealthInformationDto } from "@/generated/model/employeeHealthInformationDto";
import { FitToWorkStatus } from "@/generated/model/fitToWorkStatus";
import { useUpdateEmployeeHealthInformation } from "@/generated/people/people";
import {
  useProfileSectionForm,
  useProfileSectionSave,
} from "@/hooks/use-profile-section-form";
import { fitToWorkLabel } from "@/lib/people";
import {
  type HealthInformationFormValues,
  healthInformationFormValues,
  healthInformationRequest,
  healthInformationSchema,
} from "@/lib/people-profile-sections";

import { ProfileSectionForm } from "../ProfileSectionForm";
import { SectionRadioGroup, SectionTextArea } from "./ProfileSectionFields";

const TEXT_FIELDS: {
  key: Exclude<keyof HealthInformationFormValues, "fitToWorkStatus">;
  label: string;
}[] = [
  {
    key: "significantMedicalConditions",
    label: "Significant medical conditions",
  },
  { key: "additionalInformation", label: "Additional information" },
  { key: "reasonableAdjustments", label: "Reasonable adjustments" },
];

const FIT_TO_WORK_OPTIONS = Object.values(FitToWorkStatus).map((status) => ({
  value: status,
  label: fitToWorkLabel(status),
}));

interface EditHealthInformationFormProps {
  nurseryId: string;
  userId: string;
  healthInformation: EmployeeHealthInformationDto;
  onDone: () => void;
}

export function EditHealthInformationForm({
  nurseryId,
  userId,
  healthInformation,
  onDone,
}: EditHealthInformationFormProps) {
  const { values, setErrors, setField, errors, validate } =
    useProfileSectionForm(
      () => healthInformationFormValues(healthInformation),
      healthInformationSchema,
    );
  const update = useUpdateEmployeeHealthInformation({
    mutation: useProfileSectionSave(
      nurseryId,
      userId,
      "Health information",
      onDone,
      setErrors,
    ),
  });
  const isPending = update.isPending;

  const handleSubmit = () => {
    if (!validate()) return;
    update.mutate({
      nurseryId,
      userId,
      data: healthInformationRequest(values),
    });
  };

  return (
    <ProfileSectionForm
      testId="edit-health-information-form"
      onCancel={onDone}
      isPending={isPending}
      onSubmit={handleSubmit}
    >
      {TEXT_FIELDS.map(({ key, label }) => (
        <SectionTextArea
          key={key}
          label={label}
          name={key}
          value={values[key]}
          onChange={(value) => setField(key, value)}
          disabled={isPending}
          error={errors[key]}
        />
      ))}
      <SectionRadioGroup
        label="Fit to work"
        value={values.fitToWorkStatus}
        options={FIT_TO_WORK_OPTIONS}
        onChange={(value) => setField("fitToWorkStatus", value)}
        disabled={isPending}
      />
    </ProfileSectionForm>
  );
}
